import React, { useEffect, useRef, useState } from 'react';
import { useScrollAnimation, useParallax, useSmoothReveal } from '../utils/useScrollAnimations';
import { PhoneCall } from 'lucide-react';

const About = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredPhoto, setHoveredPhoto] = useState(null);
  const sectionRef = useRef(null);
  const parallaxY = useParallax(0.15);
  const { scrollY } = useScrollAnimation();
  
  // Smooth reveal hooka - snížený threshold pro mobilní
  const titleReveal = useSmoothReveal({ fadeDirection: 'up', threshold: 0.05, rootMargin: '50px' });
  const teamReveal = useSmoothReveal({ fadeDirection: 'scale', threshold: 0.05, rootMargin: '50px' });
  const textReveal = useSmoothReveal({ fadeDirection: 'left', threshold: 0.05, rootMargin: '50px' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="about"
      className="py-32 md:py-20 relative scroll-optimized bg-gradient-to-b from-gray-50 via-red-50/20 to-white"
    >
      {/* Simplified background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 left-1/4 w-20 h-20 bg-red-400 rounded-full filter blur-xl parallax-light"></div>
        <div className="absolute bottom-40 right-1/4 w-28 h-28 bg-red-600 rounded-full filter blur-xl parallax-light"></div>
      </div>
      
      <div className="container mx-auto px-6 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div 
            className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              transform: `translateY(${parallaxY * -0.1}px)`
            }}
          >
            {/* Nadpis s animovanými efekty */}
            <div 
              ref={titleReveal.ref}
              className="text-center mb-8 md:mb-8"
              style={titleReveal.animationStyle}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  transform: `translateY(${Math.sin(scrollY * 0.01) * 2}px) scale(${1 + Math.sin(scrollY * 0.005) * 0.02})`
                }}
              >
                <span 
                  className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent"
                  style={{
                    backgroundPosition: `${scrollY * 0.1}% 50%`,
                    backgroundSize: '200% 200%'
                  }}
                >
                  O nás
                </span>
              </h2>
              <div 
                className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto mb-6"
                style={{
                  transform: `scaleX(${1 + Math.sin(scrollY * 0.02) * 0.1})`,
                  filter: `drop-shadow(0 0 ${3 + Math.sin(scrollY * 0.03) * 2}px rgba(59, 130, 246, 0.5))`
                }}
              ></div>
            </div>

            {/* Obsah s enhanced animacemi */}
            <div 
              className="rounded-2xl p-6 md:p-8 bg-white border border-gray-200/50 shadow-sm relative overflow-hidden mx-4 md:mx-0 mt-16 md:mt-0"
              style={{
                transform: `translateY(${parallaxY * -0.05}px) scale(${1 + Math.sin(scrollY * 0.003) * 0.01})`,
                boxShadow: `0 10px 30px rgba(0,0,0,${0.1 + Math.sin(scrollY * 0.008) * 0.05})`
              }}
            >
              {/* Animated border glow */}
              <div 
                className="absolute inset-0 rounded-2xl"
                style={{
                  background: `linear-gradient(${scrollY * 0.3}deg, rgba(59, 130, 246, 0.05), rgba(34, 197, 94, 0.05))`,
                  opacity: Math.sin(scrollY * 0.01) * 0.5 + 0.3
                }}
              ></div>
              
              <div className="relative z-10">
                {/* Fotografie s enhanced animacemi */}
                <div 
                  ref={teamReveal.ref}
                  className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-8 mb-8 px-4 md:px-0"
                  style={teamReveal.animationStyle}
                >
                  <div 
                    className="text-center"
                    onMouseEnter={() => setHoveredPhoto('david')}
                    onMouseLeave={() => setHoveredPhoto(null)}
                  >
                    <div 
                      className="w-28 h-28 md:w-32 md:h-32 mx-auto mb-4 rounded-full overflow-hidden shadow-lg border-4 border-white flex items-center justify-center relative transition-all duration-500"
                      style={{
                        transform: `scale(${hoveredPhoto === 'david' ? 1.1 : 1}) rotateY(${hoveredPhoto === 'david' ? 5 : 0}deg) translateY(${Math.sin(scrollY * 0.01) * 2}px)`,
                        boxShadow: hoveredPhoto === 'david' 
                          ? '0 15px 35px rgba(59, 130, 246, 0.4)' 
                          : `0 8px 25px rgba(0,0,0,${0.3 + Math.sin(scrollY * 0.02) * 0.1})`
                      }}
                    >
                      {hoveredPhoto === 'david' && (
                        <div className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-50"></div>
                      )}
                      <img 
                        src={`${process.env.PUBLIC_URL}/david-formanek.png`} 
                        alt="David Formánek" 
                        className="w-full h-full object-cover transition-transform duration-300"
                        style={{
                          transform: hoveredPhoto === 'david' ? 'scale(1.05)' : 'scale(1)',
                          filter: hoveredPhoto === 'david' ? 'brightness(1.1) contrast(1.1)' : 'brightness(1)'
                        }}
                        onError={(e) => {
                          console.log('Error loading David image:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="text-gray-600 text-4xl font-bold hidden bg-gray-200 w-full h-full flex items-center justify-center">DF</div>
                    </div>
                    <h3 
                      className="font-semibold text-lg transition-all duration-300 text-gray-900"
                      style={{
                        transform: `translateY(${hoveredPhoto === 'david' ? -2 : 0}px)`,
                        textShadow: hoveredPhoto === 'david' ? '0 0 10px rgba(59, 130, 246, 0.5)' : 'none'
                      }}
                    >
                      David Formánek
                    </h3>
                    <a 
                      href="tel:+420775796960"
                      className="text-sm text-gray-600 hover:text-red-600 mt-1 transition-all duration-300 cursor-pointer underline decoration-dotted"
                      style={{
                        transform: `translateY(${hoveredPhoto === 'david' ? -1 : 0}px)`
                      }}
                    >
                      +420 775 796 960 / +420 776 457 223
                    </a>
                  </div>
                  
                  <div 
                    className="text-center"
                    onMouseEnter={() => setHoveredPhoto('jakub')}
                    onMouseLeave={() => setHoveredPhoto(null)}
                  >
                    <div 
                      className="w-28 h-28 md:w-32 md:h-32 mx-auto mb-4 rounded-full overflow-hidden shadow-lg border-4 border-white flex items-center justify-center relative transition-all duration-500"
                      style={{
                        transform: `scale(${hoveredPhoto === 'jakub' ? 1.1 : 1}) rotateY(${hoveredPhoto === 'jakub' ? -5 : 0}deg) translateY(${Math.sin(scrollY * 0.012) * 2}px)`,
                        boxShadow: hoveredPhoto === 'jakub' 
                          ? '0 15px 35px rgba(34, 197, 94, 0.4)' 
                          : `0 8px 25px rgba(0,0,0,${0.3 + Math.sin(scrollY * 0.025) * 0.1})`
                      }}
                    >
                      {hoveredPhoto === 'jakub' && (
                        <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-50"></div>
                      )}
                      <img 
                        src={`${process.env.PUBLIC_URL}/jakub-dufek.png`} 
                        alt="Jakub Dufek" 
                        className="w-full h-full object-cover transition-transform duration-300"
                        style={{ 
                          transform: `scale(${hoveredPhoto === 'jakub' ? 1.13 : 1.08})`,
                          filter: hoveredPhoto === 'jakub' ? 'brightness(1.1) contrast(1.1)' : 'brightness(1)'
                        }}
                        onError={(e) => {
                          console.log('Error loading Jakub image:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="text-gray-600 text-4xl font-bold hidden bg-gray-200 w-full h-full flex items-center justify-center">JD</div>
                    </div>
                    <h3 
                      className={`font-semibold text-lg transition-all duration-300 text-gray-900`}
                      style={{
                        transform: `translateY(${hoveredPhoto === 'jakub' ? -2 : 0}px)`,
                        textShadow: hoveredPhoto === 'jakub' ? '0 0 10px rgba(34, 197, 94, 0.5)' : 'none'
                      }}
                    >
                      Jakub Dufek
                    </h3>
                    <a 
                      href="tel:+420604900400"
                      className="text-sm text-gray-600 hover:text-red-600 mt-1 transition-all duration-300 cursor-pointer underline decoration-dotted"
                      style={{
                        transform: `translateY(${hoveredPhoto === 'jakub' ? -1 : 0}px)`
                      }}
                    >
                      +420 604 900 400
                    </a>
                  </div>
                </div>

                {/* Text s micro animacemi */}
                <div 
                  ref={textReveal.ref}
                  className="text-center space-y-4 px-4 md:px-0 text-gray-700"
                  style={{
                    ...textReveal.animationStyle,
                    transform: `${textReveal.animationStyle.transform} translateY(${Math.sin(scrollY * 0.005) * 1}px)`
                  }}
                >
                  <p 
                    className="text-base md:text-lg leading-relaxed transition-all duration-300"
                    style={{
                      transform: `translateX(${Math.sin(scrollY * 0.003) * 1}px)`
                    }}
                  >
                    Jsme David Formánek a Jakub Dufek, dva kolegové s více než 8 lety zkušeností v telekomunikacích. 
                    Spojuje nás nadšení pro inovativní řešení, osobní přístup a chuť posouvat služby Vodafone na vyšší úroveň.
                  </p>
                  <p 
                    className="text-base md:text-lg leading-relaxed transition-all duration-300"
                    style={{
                      transform: `translateX(${Math.sin(scrollY * 0.004) * -1}px)`
                    }}
                  >
                    Naším cílem je pomáhat firmám růst díky moderním technologiím, chytrým nástrojům a férové spolupráci. 
                    Nejsme jen dodavatelé služeb, jsme partneři, na které se můžete spolehnout.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
